import type { InlineConfig } from 'vite';
import tsconfigPaths from '@wix/vite-tsconfig-paths';
import externalize from 'vite-plugin-externalize-dependencies';
import { SITE_COMPONENTS_VITE_CACHE_DIR } from '@wix/cli-app-definitions';
import type { ProjectModel } from '../../../model.js';
import { moduleBundleDevSiteComponents } from '../../plugins/module-bundle/plugin.js';
import type { ViteDevConfigOptions } from '../../vite-config-options.js';
import { draftJs } from '../../plugins/draft-js.js';
import { ssl } from '../../plugins/ssl.js';
import { hmrSiteComponents } from '../../plugins/hmr/plugin.js';
import { autoOrigin } from '../../plugins/auto-origin.js';
import {
  SITE_COMPONENTS_PATTERN,
  SITE_COMPONENTS_PREVIEW_PATTERN,
  SITE_COMPONENTS_SDK_PATTERN,
  SITE_COMPONENTS_PRESETS_PATTERN,
} from '../../../dev-center/components/site/components.js';
import { monitoring } from '../../plugins/monitoring.js';
import { fbjsHotfixPlugin } from '../../plugins/fbjs-hotfix-plugin.js';
import { webMethodsPlugin } from '../../plugins/web-methods-plugin.js';
import { siteComponentsExternals } from '../shared.js';

export const SITE_COMPONENTS_BASE = '/@gateway/site_components';

export function getViteConfig(
  model: ProjectModel,
  options: ViteDevConfigOptions
): InlineConfig {
  const { https, baseBackendUrl } = options;

  return {
    appType: 'custom',
    configFile: false,
    root: model.projectFolder,
    customLogger: options.customLogger,
    clearScreen: false,
    logLevel: 'info',
    publicDir: false,
    base: SITE_COMPONENTS_BASE,
    server: {
      cors: { origin: true },
    },
    cacheDir: SITE_COMPONENTS_VITE_CACHE_DIR,
    optimizeDeps: {
      include: ['@wix/sdk-context'],
      entries: [
        SITE_COMPONENTS_PATTERN,
        SITE_COMPONENTS_PREVIEW_PATTERN,
        SITE_COMPONENTS_SDK_PATTERN,
        SITE_COMPONENTS_PRESETS_PATTERN,
      ],
    },
    define: {
      ...(baseBackendUrl
        ? {
            'import.meta.env.BASE_API_URL': JSON.stringify(
              `${baseBackendUrl.replace(/\/$/, '')}/functions`
            ),
          }
        : {}),
    },
    plugins: [
      webMethodsPlugin({
        projectFolder: model.projectFolder,
      }),
      externalize({
        externals: siteComponentsExternals,
      }),
      fbjsHotfixPlugin(),
      draftJs(),
      ...(https ? [ssl()] : []),
      moduleBundleDevSiteComponents({
        initialModel: model,
        projectModelWatcher: options.projectModelWatcher,
      }),
      hmrSiteComponents(),
      autoOrigin(),
      monitoring({ initialModel: model }),
      tsconfigPaths({
        ignoreConfigErrors: true,
        root: model.projectFolder,
      }),
    ],
  };
}
